import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Settings, AlertCircle, Save } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SiteSetting {
  key: string;
  label: string;
  value: string;
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function fetchSettings(): Promise<SiteSetting[]> {
  const res = await apiClient.get('/settings');
  return ((res.data as any).data?.settings ?? []) as SiteSetting[];
}

async function saveSettings(settings: Record<string, string>): Promise<void> {
  await apiClient.post('/settings', { settings });
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/** Keys whose values are too long / multiline — rendered full-width with a textarea */
const MULTILINE_KEYS = new Set(['site_logo_url', 'support_maps_embed_url', 'support_address']);

/**
 * Explicit display order so related fields appear side-by-side.
 * Any key not listed here will be appended at the end automatically.
 */
const FIELD_ORDER = [
  'site_url',
  'social_linkedin_url',
  'social_twitter_url',
  'support_mail',
  'support_phone',
  'trial_period_days',
  'support_address',
  'support_maps_embed_url',
  'site_logo_url',
];

function sortSettings(settings: SiteSetting[]): SiteSetting[] {
  const known = FIELD_ORDER
    .map((key) => settings.find((s) => s.key === key))
    .filter(Boolean) as SiteSetting[];
  const unknown = settings.filter((s) => !FIELD_ORDER.includes(s.key));
  return [...known, ...unknown];
}

const textareaClass =
  'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-y min-h-[90px]';

// ---------------------------------------------------------------------------
// SiteSettingsPage
// ---------------------------------------------------------------------------

export default function SiteSettingsPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const { data: settings, isLoading, isError, error } = useQuery({
    queryKey: ['admin-site-settings'],
    queryFn: fetchSettings,
  });

  useEffect(() => {
    if (settings && settings.length > 0 && Object.keys(form).length === 0) {
      const initial: Record<string, string> = {};
      settings.forEach((s) => { initial[s.key] = s.value ?? ''; });
      setForm(initial);
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSettings(form);
      toast.success('Settings saved successfully.');
      qc.invalidateQueries({ queryKey: ['admin-site-settings'] });
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const canSave = !isLoading && !isError && !!settings?.length;

  return (
    <div className="space-y-6">

      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Site Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage global site configuration values stored in the database.
          </p>
        </div>
        <Button onClick={handleSave} disabled={!canSave || saving} className="flex-shrink-0">
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>

      {/* Settings card — full width */}
      <div className="rounded-xl border bg-card overflow-hidden">

        {/* Card header */}
        <div className="flex items-center gap-3 border-b bg-muted/30 px-6 py-4">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-background border">
            <Settings className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground">Configuration</h2>
            <p className="text-xs text-muted-foreground">
              Key-value settings applied across the platform. New entries added to the database appear here automatically.
            </p>
          </div>
        </div>

        {/* Card body */}
        <div className="p-6">

          {/* Loading skeleton — 2-col grid */}
          {isLoading && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="space-y-1.5">
                  <div className="h-4 w-28 rounded bg-muted animate-pulse" />
                  <div className="h-9 w-full rounded bg-muted animate-pulse" />
                </div>
              ))}
            </div>
          )}

          {/* Error state */}
          {isError && (
            <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-4">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
              <div>
                <p className="text-sm font-medium text-destructive">Failed to load settings</p>
                <p className="mt-0.5 text-xs text-destructive/80">
                  {error instanceof Error ? error.message : 'An unexpected error occurred.'}
                </p>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !isError && settings?.length === 0 && (
            <p className="py-4 text-sm text-muted-foreground">
              No settings found. Add rows to the{' '}
              <span className="font-mono text-xs bg-muted px-1 py-0.5 rounded">site_settings</span>{' '}
              table and they will appear here automatically.
            </p>
          )}

          {/* Fields — 2-column grid; multiline fields span full width */}
          {!isLoading && !isError && settings && settings.length > 0 && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-5">
              {sortSettings(settings).map((setting) => {
                const isMultiline = MULTILINE_KEYS.has(setting.key);
                return (
                  <div
                    key={setting.key}
                    className={`space-y-1.5${isMultiline ? ' col-span-2' : ''}`}
                  >
                    <Label htmlFor={setting.key} className="text-sm font-medium">
                      {setting.label}
                    </Label>
                    {isMultiline ? (
                      <textarea
                        id={setting.key}
                        className={textareaClass}
                        value={form[setting.key] ?? ''}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, [setting.key]: e.target.value }))
                        }
                        placeholder={`Enter ${setting.label.toLowerCase()}…`}
                      />
                    ) : (
                      <Input
                        id={setting.key}
                        value={form[setting.key] ?? ''}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, [setting.key]: e.target.value }))
                        }
                        placeholder={`Enter ${setting.label.toLowerCase()}…`}
                      />
                    )}
                    <p className="text-xs text-muted-foreground font-mono">{setting.key}</p>
                  </div>
                );
              })}
            </div>
          )}

        </div>

        {/* Card footer */}
        {canSave && (
          <div className="flex items-center justify-end gap-3 border-t bg-muted/20 px-6 py-4">
            <p className="text-xs text-muted-foreground flex-1">
              Changes are saved globally and take effect immediately.
            </p>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        )}

      </div>
    </div>
  );
}
